import type { TaskState } from 'src/components/pages/taskManagement/TaskStatusBadge';
import { T } from 'src/shared/helpers';

// Imported from the leaf rather than the taskManagement barrel: the barrel re-exports every task component,
// so a value-import would pull the whole styled-components tree in. A type-only leaf import is erased entirely.

export const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
};

export const formatElapsedTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    } else {
        return `${seconds}s`;
    }
};

export const getTaskTypeLabel = (taskType: string): string => {
    switch (taskType) {
        case 'SWITCH_MASTER_INTEGRATION':
            return T._('Switch Master Integration', 'Admin');
        default:
            return taskType;
    }
};

/** Numeric task state → TaskState for display. Anything unrecognised reads as WAITING. */
export const toTaskState = (state: number): TaskState =>
    state === 0 ? 'WAITING' : state === 1 ? 'PROCESSING' : state === 2 ? 'COMPLETED' : state === 3 ? 'ERROR' : 'WAITING';

/**
 * Numeric task state → TaskState for useTaskPolling. Intentionally NOT toTaskState: anything that is not
 * Waiting/Processing/Completed reads as ERROR, which stops polling. The backend's task enum has states beyond 0-3
 * (productDuplicationTasks handles a 5 = Cancelled), and mapping those to WAITING would poll them forever.
 */
export const toTaskStateForPolling = (state: number): TaskState => (state === 0 ? 'WAITING' : state === 1 ? 'PROCESSING' : state === 2 ? 'COMPLETED' : 'ERROR');

export interface SwitchProgressInput {
    state: number;
    taskState: TaskState;
    createdOn: string;
    /** Wall clock, passed in rather than read from Date.now() so this stays pure and stays tied to the caller's tick. */
    now: number;
    delayMinutes: number;
    productsToSwitch: number;
    areSwitchedSCApiKeys: boolean;
    areSwitchedSCEcommerceSettings: boolean;
    switchedProducts: ReadonlyArray<number> | null;
    areSalesChannelDetailsSwitched: boolean;
    areOrdersSalesChannelsSwitched: boolean;
}

export interface SwitchProgress {
    progress: number;
    isInPreparationPhase: boolean;
    displayTaskState: TaskState;
}

/**
 * The switch task sits idle for delayMinutes before the backend picks it up, so the bar is driven in two phases:
 * a fake time-based ramp to 50%, then real step completion from 50% to 100%.
 *
 * All three outputs come from one expression tree because they share hasDelay/elapsedMs — deriving them separately
 * invites drift. Note the arithmetic is deliberately unclamped in both directions and rounds the *sum*, not the parts.
 */
export const computeSwitchProgress = ({
    state,
    taskState,
    createdOn,
    now,
    delayMinutes,
    productsToSwitch,
    areSwitchedSCApiKeys,
    areSwitchedSCEcommerceSettings,
    switchedProducts,
    areSalesChannelDetailsSwitched,
    areOrdersSalesChannelsSwitched
}: SwitchProgressInput): SwitchProgress => {
    const totalSteps = 4 + productsToSwitch;
    let completedSteps = 0;
    if (areSwitchedSCApiKeys) completedSteps++;
    if (areSwitchedSCEcommerceSettings) completedSteps++;
    if (switchedProducts && switchedProducts.length > 0) completedSteps += switchedProducts.length;
    if (areSalesChannelDetailsSwitched) completedSteps++;
    if (areOrdersSalesChannelsSwitched) completedSteps++;

    const createdOnMs = new Date(createdOn).getTime();
    const delayMs = (delayMinutes || 0) * 60 * 1000;
    const elapsedMs = Math.max(0, now - createdOnMs);

    // When delayMinutes is 0 skip the preparation and fake phases entirely: real steps map straight to 0–100%.
    const PREPARATION_MS = 10 * 60 * 1000;
    // Only enable the two-phase system when there is enough delay for both preparation and fake progress.
    const hasDelay = delayMs > PREPARATION_MS;
    const isInPreparationPhase = hasDelay && taskState === 'WAITING' && elapsedMs < PREPARATION_MS;

    // First 50%: fake time-based progress from the 10-minute mark to delayMinutes.
    // Once real execution has started (completedSteps > 0), lock fake at 50% so the bar never goes backward.
    const fakeDurationMs = delayMs - PREPARATION_MS;
    const fakeProgress = !hasDelay
        ? 0
        : completedSteps > 0
          ? 50
          : isInPreparationPhase || fakeDurationMs <= 0
            ? 0
            : Math.min(((elapsedMs - PREPARATION_MS) / fakeDurationMs) * 50, 50);

    // When no delay: real steps cover 0–100%. With delay: real steps cover 50–100%.
    const realProgress = totalSteps > 0 ? (completedSteps / totalSteps) * (hasDelay ? 50 : 100) : 0;

    const progress = state === 2 ? 100 : Math.round(fakeProgress + realProgress);

    // WAITING during preparation, PROCESSING during fake/real progress
    const displayTaskState: TaskState = isInPreparationPhase ? 'WAITING' : taskState === 'WAITING' ? 'PROCESSING' : taskState;

    return { progress, isInPreparationPhase, displayTaskState };
};

/** URL of the Tasks tab on the Sales Channels page, where the switch task list lives. */
export const SWITCH_TASKS_TAB_URL = '/sales-channels?tab=tasks';
